/**
 * One partner's capital and drawing entries, and amending them (P1-BR7, BR-08a).
 *
 * The Partners screen shows three sums. This is what they are made of — and
 * without it there was no way to find the one wrong entry among twenty, which
 * is why the totals looked frozen even though the API could always change them.
 *
 * The admin can amend an entry here (BR-08a, D-017). Read D-017 before
 * changing anything: an amended amount takes effect without a second partner
 * approving it, and ownership follows from these figures (BR-01), so saving
 * this form moves every partner's share of the farm. Three things in the UI
 * exist because of that and are not decoration:
 *
 *   The reason is required and the button stays disabled without one. The
 *   server refuses too (AP-1) — this only means a partner finds out before
 *   typing an amount rather than after.
 *
 *   The previous figure is shown next to the box while editing, so the person
 *   changing it sees what they are replacing.
 *
 *   An amended entry is labelled, with its reason, to every partner who opens
 *   this panel — not just the admin who made the change. An amendment the
 *   others cannot see is the failure this system was built to prevent.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { formatDate } from '../../lib/date.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAmendTransaction, usePartnerLedger, type PartnerLedgerLine } from './api.js';

/** Digits with at most two decimals — checked as text, never parsed (MONEY-1). */
const MONEY_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

function AmendForm({ line, onDone }: { line: PartnerLedgerLine; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const amend = useAmendTransaction();

  const [amount, setAmount] = useState(line.amount);
  const [txnDate, setTxnDate] = useState(line.txnDate);
  const [reason, setReason] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const changed = amount !== line.amount || txnDate !== line.txnDate;
  const canSubmit = MONEY_DRAFT.test(amount) && reason.trim().length >= 5 && changed && !amend.isPending;

  async function submit(): Promise<void> {
    setErrorKey(null);
    setFieldErrors({});

    try {
      await amend.mutateAsync({
        id: line.id,
        // Only what actually moved, so the audit row's before/after is about
        // the change rather than about every field being rewritten.
        ...(amount !== line.amount ? { amount } : {}),
        ...(txnDate !== line.txnDate ? { txnDate } : {}),
        reason: reason.trim(),
      });
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form
      className="mt-3 rounded-lg bg-amber-50 p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor={`amount-${String(line.id)}`}>
        {t('partnerLedger.amount')}
      </label>
      <input
        id={`amount-${String(line.id)}`}
        inputMode="decimal"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 tabular-nums"
      />
      {/* What is being replaced, in front of the person replacing it. */}
      <p className="mt-1 text-xs text-gray-600">{t('partnerLedger.was', { amount: formatMoney(line.amount) })}</p>
      {fieldErrors['amount'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['amount']}</p>}

      <label className="mb-1 mt-3 block text-xs font-medium text-gray-700" htmlFor={`date-${String(line.id)}`}>
        {t('partnerLedger.date')}
      </label>
      <input
        id={`date-${String(line.id)}`}
        type="date"
        value={txnDate}
        onChange={(event) => setTxnDate(event.target.value)}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2"
      />
      {fieldErrors['txnDate'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['txnDate']}</p>}

      <label className="mb-1 mt-3 block text-xs font-medium text-gray-700" htmlFor={`reason-${String(line.id)}`}>
        {t('partnerLedger.reason')}
      </label>
      <input
        id={`reason-${String(line.id)}`}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        maxLength={500}
        className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2"
      />
      <p className="mt-1 text-xs text-gray-600">{t('partnerLedger.reasonHint')}</p>
      {fieldErrors['reason'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['reason']}</p>}

      {errorKey !== null && (
        <div role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-touch flex-1 rounded-lg bg-green-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t(amend.isPending ? 'partnerLedger.saving' : 'partnerLedger.save')}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-touch rounded-lg bg-white px-3 py-2 text-sm text-gray-700"
        >
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}

function LedgerRow({ line, canAmend }: { line: PartnerLedgerLine; canAmend: boolean }): JSX.Element {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const isDrawing = line.type === 'drawing';

  return (
    <li className="border-b border-gray-100 py-3 last:border-b-0">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-800">
            {t(isDrawing ? 'partnerMoney.tookOut' : 'partnerMoney.putIn')}
            {line.status !== 'approved' && (
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                {t(`finance.list.filter.${line.status}`)}
              </span>
            )}
          </p>
          <p className="text-xs text-gray-500">{formatDate(line.txnDate)}</p>
        </div>
        <span className={`shrink-0 tabular-nums font-medium ${isDrawing ? 'text-gray-900' : 'text-green-700'}`}>
          {isDrawing ? '−' : '+'} {formatMoney(line.amount, { symbol: false })}
        </span>
      </div>

      {/* Visible to every partner, not only the admin who made the change. */}
      {line.amendReason !== null && (
        <p className="mt-1 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-900">
          {t('partnerLedger.amended', { reason: line.amendReason })}
        </p>
      )}

      {canAmend && !isEditing && (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="mt-2 min-h-touch text-sm font-medium text-green-800 underline"
        >
          {t('partnerLedger.edit')}
        </button>
      )}

      {canAmend && isEditing && <AmendForm line={line} onDone={() => setIsEditing(false)} />}
    </li>
  );
}

export function PartnerLedgerPanel({ partnerId }: { partnerId: number }): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, isLoading, isError, refetch } = usePartnerLedger(partnerId);

  // BR-08a — amending is admin-only. The server refuses anyone else regardless
  // of what this renders (AP-1).
  const canAmend = user?.role === 'admin';

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      {isLoading && (
        <p role="status" className="py-4 text-center text-sm text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          <p className="mb-1">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {data && data.lines.length === 0 && (
        <p className="py-4 text-center text-sm text-gray-500">{t('partnerLedger.empty')}</p>
      )}

      {data && data.lines.length > 0 && (
        <ul>
          {data.lines.map((line) => (
            <LedgerRow key={line.id} line={line} canAmend={canAmend} />
          ))}
        </ul>
      )}
    </div>
  );
}
